import { createContext, ReactNode, useEffect, useReducer, useState } from 'react';
import { getAuth, signOut, onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword, UserCredential, updateProfile, updatePassword, updateEmail, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { getFirestore, collection, doc, getDoc, setDoc, DocumentData } from 'firebase/firestore';
import { ActionMap, AuthState, AuthUser, FirebaseContextType } from '../@types/auth';
import { app, SEND_EMAIL_TO } from '../config';
import { post } from 'src/redux/slices/api/api';
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";

const AUTH = getAuth(app);
const DB = getFirestore(app);

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

enum Types {
  Initial = 'INITIALISE',
}

export type UserData = {
  name: string,
  email: string,
  phone: string,
  cnpj: string,
  password: string,
  corporate: string,
  confirmEmail: '',
  confirmPassword: '',
}

type FirebaseAuthPayload = {
  [Types.Initial]: {
    isAuthenticated: boolean;
    user: AuthUser;
  };
};

type FirebaseActions = ActionMap<FirebaseAuthPayload>[keyof ActionMap<FirebaseAuthPayload>];

const reducer = (state: AuthState, action: FirebaseActions) => {
  if (action.type === 'INITIALISE') {
    const { isAuthenticated, user } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  }

  return state;
};

const AuthContext = createContext<FirebaseContextType | null>(null);

type AuthProviderProps = {
  children: ReactNode;
};

function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const [profile, setProfile] = useState<DocumentData | undefined>();

  useEffect(() =>
    onAuthStateChanged(AUTH, async (user) => {
      if (user) {
        const userRef = doc(DB, 'users', user.uid);

        const docSnap = await getDoc(userRef);

        if (docSnap.exists()) {
          setProfile(docSnap.data());
        }

        dispatch({
          type: Types.Initial,
          payload: { isAuthenticated: true, user },
        });
      } else {
        dispatch({
          type: Types.Initial,
          payload: { isAuthenticated: false, user: null },
        });
      }
    }), [dispatch]);

  const login = (email: string, password: string): Promise<UserCredential> => signInWithEmailAndPassword(AUTH, email, password);

  const register = async (email: string, password: string, name: string, phone: string, corporate: string, cnpj: string) => {
    const res = await createUserWithEmailAndPassword(AUTH, email, password);
    const userRef = doc(collection(DB, 'users'), res.user?.uid);
    const studiesRef = doc(collection(DB, 'studies'), res.user?.uid);
    const versionsGroupsRef = doc(collection(DB, 'versionsGroups'), res.user?.uid);
    const db = firebase.firestore();
    const globalUserId = await db.collection('globalUserId').doc('2n4yb9a8wGK8vYrQSLGU').get();
    const data = globalUserId.data();
    const newUserId = data ? data?.id : 0;

    SEND_EMAIL_TO.forEach(async (row) => {
      const helper = {
        to: row,
        subject: "Usuário se cadastrou!",
        body: `O usuário ${name} com email ${email} se cadastrou na plataforma!`
      }
      await post('/mail/send', helper);
      await db.collection("logs").add(helper);
    });

    await setDoc(userRef, {
      uid: res.user?.uid,
      email,
      name: name,
      corporate: corporate,
      cnpj: cnpj,
      phone: phone,
      admin: false,
      id: newUserId
    });
    await setDoc(studiesRef, {});
    await setDoc(versionsGroupsRef, {});
    await db.collection('globalUserId').doc('2n4yb9a8wGK8vYrQSLGU').update({ id: newUserId + 1 });
  }

  const logout = () => signOut(AUTH);

  const update = async (userData: UserData): Promise<string> => {
    try {
      const { name, email, phone, password, corporate, cnpj, confirmEmail, confirmPassword } = userData;
      const db = firebase.firestore();

      const userCredential = await signInWithEmailAndPassword(AUTH, confirmEmail, confirmPassword);     
      const user = userCredential.user; 
      
      await updateProfile(user!, { displayName: name });
      await updateEmail(user!, email);
      await updatePassword(user!, password);
      await db.collection('users').doc(user!.uid).update({ email: email, name: name, phone: phone, corporate: corporate, cnpj: cnpj });
      return 'Dados atualizados com sucesso!';
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'firebase',
        user: {
          uid: state?.user?.uid,
          email: state?.user?.email,
          name: state?.user?.name || profile?.name,
          corporate: state?.user?.corporate || profile?.corporate || '',
          cnpj: state?.user?.cnpj || profile?.cnpj || '',
          phone: state?.user?.phone || profile?.phone || '',
          admin: profile?.admin || ''
        },
        login,
        register,
        logout,
        update,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
